<template>
    <div class="box hosting-add-on">
        <div class="box-content">
            <div class="icon-container">
                <i class="far fa-server" />
            </div>

            <div class="content-container">
                <h2 class="header">
                    Easy Hosting
                </h2>

                <p class="description">
                    All Paid plans include Easy Hosting, our simple to use podcast hosting platform built for podcasters like you.
                    <template v-if="!hasHostingFeature">
                        Upgrade your account to take advantage of our podcast hosting platform.
                    </template>
                    <a :href="learnMoreLink">Learn More</a>
                </p>
            </div>

            <div class="switch-container">
                <p class="switch-title">
                    {{ switchTitle }}
                </p>

                <div class="switch">
                    <toggle-switch
                        :value="activated"
                        :locked="true"
                        @click="handleToggle"
                    />

                    <div class="switch-status">
                        {{ activated ? 'Activated' : 'Deactivated' }}
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>

/**
 * External dependencies
 */
import { isEqual } from 'lodash';
import { mapState, mapMutations } from 'vuex';

/**
 * Internal dependencies
 */
import { Feature, hasFeature } from '@/utils/show';
import { Step } from '@/utils/config/addShowModal';
import { steps as downgradeSteps } from '@/utils/config/showHostingDowngradeModal';
import { steps as upgradeSteps } from '@/utils/config/upgradeShowModal';
import ToggleSwitch from '@/components/Controls/ToggleSwitch';

export default {
    components: {
        ToggleSwitch,
    },

    props: {
        show: {
            type: Object,
            default: () => ({}),
        },
    },

    data: () => ({
        activated: false,
        learnMoreLink: 'https://fusebox.fm/hosting',
    }),

    computed: {
        ...mapState({
            user: state => state.user || {},
        }),

        /**
         * Checks if the show plan has a hosting feature enabled.
         *
         * @returns {boolean} `true` it hosting feature is enabled in current
         *                     plan.
         */
        hasHostingFeature() {
            return hasFeature(this.show, Feature.EasyHosting);
        },

        /**
         * Returns switch title based on the plan hosting feature.
         *
         * @returns {string} Switch title.
         */
        switchTitle() {
            return this.hasHostingFeature ? 'Included' : 'Upgrade Now';
        },
    },

    watch: {
        show: {
            immediate: true,

            /**
             * Manage data.
             *
             * @param {object} show Show object data.
             * @param {*} oldShow Show object data.
             */
            handler(show, oldShow) {
                if (isEqual(show, oldShow)) {
                    return;
                }

                this.activated = show.hosted || false;
            },
        },
    },

    methods: {
        ...mapMutations(['changeUIState']),

        /**
         * Handles "activated" switch change.
         *
         * @param  {boolean} activated Activated value.
         */
        handleToggle(activated) {
            if (activated) {
                this.runUpgradeProcess();
                return;
            }

            this.runDowngradeProcess();
        },

        /**
         * Runs upgrade process.
         */
        runUpgradeProcess() {
            if (! this.hasHostingFeature) {
                this.changeUIState({
                    addShowModal: {
                        mode: 'upgrade',
                        show: this.show,
                        step: Step.Plans,
                        type: 'hosted',
                        visible: true,
                    },
                });

                return;
            }

            this.changeUIState({
                upgradeShowModal: {
                    visible: true,
                    show: this.show,
                    step: upgradeSteps.intro,
                    addOns: ['hosting'],
                },
            });
        },

        /**
         * Runs downgrade process.
         */
        runDowngradeProcess() {
            this.changeUIState({
                showHostingDowngradeModal: {
                    visible: true,
                    show: this.show,
                    step: downgradeSteps.intro,
                },
            });
        },
    },
};
</script>
